import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Search, Loader2, Download, FileText,
  ExternalLink, CheckCircle, X as XIcon, DollarSign,
  Globe, AlertTriangle, Shield, Package,
  ChevronDown, ChevronUp, Building2, ArrowUpDown,
  Lock, Unlock, Edit3, Plus, Trash2, Send, Check,
  XCircle, RotateCcw, Clock, PenLine, Sparkles, UserPlus,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/services/api';
import InfoTip from '@/components/InfoTip';
import { useCurrency } from '@/context/CurrencyContext';
import { useBranding } from '@/context/BrandingContext';
import { createBrandedPDF, addSectionTitle, addMetricCards, addTable, downloadPDF, addText } from '@/utils/pdfExport';
import QuickAssignModal from '@/components/QuickAssignModal';

const fmt = (v) => {
  if (v == null || v === '' || isNaN(v) || v === 0) return '-';
  return `$${Number(v).toFixed(4)}`;
};

const fmtTotal = (v) => {
  if (v == null || v === '' || isNaN(v) || v === 0) return '-';
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const riskColors = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-red-100 text-red-800',
};

const QUOTE_STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: Edit3 },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Send },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-800 border-green-300', icon: Check },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle },
  revised: { label: 'Revised', color: 'bg-amber-100 text-amber-800 border-amber-300', icon: RotateCcw },
};

const LINE_STATUS_CONFIG = {
  priced: { label: 'Priced', color: 'bg-green-100 text-green-800' },
  manual: { label: 'Manual', color: 'bg-blue-100 text-blue-800' },
  tbd: { label: 'TBD', color: 'bg-amber-100 text-amber-800' },
  excluded: { label: 'Excluded', color: 'bg-gray-100 text-gray-500 line-through' },
};

const SimulatorDetail = () => {
  const { simId } = useParams();
  const navigate = useNavigate();
  const { format } = useCurrency();
  const { branding } = useBranding();

  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('quote');
  const [expandedMpn, setExpandedMpn] = useState(null);

  // Line override editing
  const [editingLine, setEditingLine] = useState(null); // mpn being edited
  const [editPrice, setEditPrice] = useState('');
  const [editMargin, setEditMargin] = useState('');
  const [saving, setSaving] = useState(false);

  // Additional costs
  const [showAddCost, setShowAddCost] = useState(false);
  const [newCostName, setNewCostName] = useState('');
  const [newCostType, setNewCostType] = useState('one_time');
  const [newCostAmount, setNewCostAmount] = useState('');
  const [newCostNote, setNewCostNote] = useState('');

  // Status workflow
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');

  // AI Recommendations
  const [recommendations, setRecommendations] = useState(null);
  const [recoLoading, setRecoLoading] = useState(false);

  // Assign Action
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignContext, setAssignContext] = useState({});

  const fetchSim = useCallback(async () => {
    try {
      const res = await api.get(`/simulator/${simId}`);
      setSim(res.data);
    } catch {
      toast.error('Failed to load simulation');
    }
    setLoading(false);
  }, [simId]);

  const fetchRecommendations = useCallback(async (regen = false) => {
    setRecoLoading(true);
    try {
      const res = await api.get(`/simulator/${simId}/recommendations${regen ? '?regenerate=true' : ''}`);
      setRecommendations(res.data);
    } catch { toast.error('Failed to load recommendations'); }
    setRecoLoading(false);
  }, [simId]);

  useEffect(() => { fetchSim(); }, [fetchSim]);

  useEffect(() => {
    if (!sim || sim.status !== 'processing') return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/simulator/${simId}/status`);
        if (res.data.status === 'completed') { clearInterval(interval); fetchSim(); }
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [sim, simId, fetchSim]);

  const results = sim?.results || [];
  const summary = sim?.summary || {};
  const companyName = sim?.ems_branding?.company_name || '';
  const productionQty = summary.production_quantity || 1;
  const additionalCosts = sim?.additional_costs || [];
  const quoteStatus = sim?.quote_status || 'draft';
  const statusHistory = sim?.status_history || [];

  const riskSummary = summary.risk_summary || {};
  const highRiskCount = (riskSummary.high || 0) + (riskSummary.medium || 0);
  const unmatchedCount = results.filter(r => !r.matched).length;
  const tbdCount = results.filter(r => r.line_status === 'tbd').length;

  const filtered = results.filter(r => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return r.mpn?.toLowerCase().includes(s) || r.cpn?.toLowerCase().includes(s) || r.make?.toLowerCase().includes(s) || r.description?.toLowerCase().includes(s);
  });

  // ─── LINE OVERRIDE ACTIONS ───
  const startEditLine = (r) => {
    setEditingLine(r.mpn);
    setEditPrice(r.override_price != null ? String(r.override_price) : '');
    setEditMargin(r.override_margin != null ? String(r.override_margin) : '');
  };

  const saveLineOverride = async (mpn, extraFields = {}) => {
    setSaving(true);
    try {
      const ov = { mpn, ...extraFields };
      if (editingLine === mpn) {
        if (editPrice !== '') ov.override_price = parseFloat(editPrice);
        else ov.override_price = null;
        if (editMargin !== '') ov.override_margin = parseFloat(editMargin);
        else ov.override_margin = null;
      }
      const res = await api.patch(`/simulator/${simId}/line-overrides`, { overrides: [ov] });
      if (res.data.success) {
        await fetchSim();
        setEditingLine(null);
        toast.success('Line updated');
      }
    } catch { toast.error('Failed to save override'); }
    setSaving(false);
  };

  const toggleLock = async (r) => {
    await saveLineOverride(r.mpn, { locked: !r.locked });
  };

  const setLineStatus = async (mpn, status) => {
    await saveLineOverride(mpn, { line_status: status });
  };

  const saveManualPrice = async (mpn, price) => {
    setSaving(true);
    try {
      const res = await api.patch(`/simulator/${simId}/line-overrides`, {
        overrides: [{ mpn, manual_price: parseFloat(price), line_status: 'manual' }]
      });
      if (res.data.success) {
        await fetchSim();
        toast.success('Manual price saved');
      }
    } catch { toast.error('Failed to save price'); }
    setSaving(false);
  };

  // ─── ADDITIONAL COSTS ACTIONS ───
  const addCost = async () => {
    if (!newCostName.trim() || !newCostAmount) return;
    try {
      const res = await api.post(`/simulator/${simId}/additional-costs`, {
        name: newCostName, cost_type: newCostType, amount: parseFloat(newCostAmount), note: newCostNote,
      });
      if (res.data.success) {
        await fetchSim();
        setNewCostName(''); setNewCostAmount(''); setNewCostNote('');
        setShowAddCost(false);
        toast.success('Cost added');
      }
    } catch { toast.error('Failed to add cost'); }
  };

  const removeCost = async (costId) => {
    try {
      const res = await api.delete(`/simulator/${simId}/additional-costs/${costId}`);
      if (res.data.success) { await fetchSim(); toast.success('Cost removed'); }
    } catch { toast.error('Failed to remove cost'); }
  };

  // ─── QUOTE STATUS ACTIONS ───
  const updateQuoteStatus = async () => {
    try {
      const res = await api.patch(`/simulator/${simId}/quote-status`, { quote_status: pendingStatus, note: statusNote });
      if (res.data.success) {
        await fetchSim();
        setStatusDialogOpen(false);
        setStatusNote('');
        toast.success(`Quote marked as ${pendingStatus}`);
      }
    } catch { toast.error('Failed to update status'); }
  };

  const openStatusDialog = (status) => { setPendingStatus(status); setStatusDialogOpen(true); };

  // ─── EXPORTS ───
  const handleCSVExport = async () => {
    try {
      const res = await api.get(`/simulator/${simId}/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `quote-${sim?.project_name || simId}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const handlePDFExport = () => {
    if (!sim || !results.length) return;
    const company = companyName || '1Data';
    const doc = createBrandedPDF(company, `Quotation: ${sim.project_name || 'Project'}`, branding);
    let y = doc._startY;

    // Quote header
    y = addSectionTitle(doc, sim.project_name || 'Quotation', y);
    y = addMetricCards(doc, [
      { label: 'Components', value: summary.total_components || 0 },
      { label: 'Production Qty', value: `${productionQty.toLocaleString()} boards` },
      { label: 'Quote Total', value: fmtTotal(summary.grand_total || summary.total_quoted_cost) },
    ], y);
    y += 4;

    // Component pricing table — customer sees unit price and line total only
    y = addSectionTitle(doc, 'Bill of Materials', y);
    y = addTable(doc,
      ['#', 'MPN', 'Description', 'Qty/Board', 'Order Qty', 'Unit Price ($)', 'Line Total ($)'],
      results.filter(r => r.line_status !== 'excluded').map((r, i) => [
        String(i + 1), r.mpn, r.description || '-',
        String(r.qty_per_board || '-'), String(r.order_qty?.toLocaleString() || '-'),
        fmt(r.quoted_price), fmtTotal(r.line_total),
      ]),
      y
    );

    // Additional costs — these are legitimate customer-facing charges
    if (additionalCosts.length > 0) {
      y = addSectionTitle(doc, 'Additional Costs', y);
      y = addTable(doc,
        ['Item', 'Type', 'Amount ($)'],
        additionalCosts.map(c => {
          const orderTotal = c.cost_type === 'per_board' ? c.amount * productionQty : c.amount;
          return [c.name, c.cost_type === 'per_board' ? `Per Board ($${c.amount}/board)` : 'One-Time', `$${orderTotal.toLocaleString()}`];
        }),
        y
      );
    }

    // Quote summary — no margin breakdown, just totals
    y = addSectionTitle(doc, 'Quote Summary', y);
    const summaryRows = [['BOM Total', fmtTotal(summary.total_quoted_cost)]];
    if (additionalCosts.length > 0) summaryRows.push(['Additional Costs', fmtTotal(summary.additional_costs_total || 0)]);
    summaryRows.push(['GRAND TOTAL', fmtTotal(summary.grand_total || summary.total_quoted_cost)]);
    if (productionQty > 1) summaryRows.push(['Cost Per Board', fmtTotal((summary.grand_total || summary.total_quoted_cost) / productionQty)]);
    y = addTable(doc, ['Description', 'Amount'], summaryRows, y);

    // Terms & Validity
    const terms = [];
    terms.push(`Quote Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`);
    terms.push('Validity: 30 days from quote date');
    terms.push(`Production Quantity: ${productionQty.toLocaleString()} boards`);
    const constraints = sim.constraints || {};
    if (constraints.target_incoterm) terms.push(`Incoterm: ${constraints.target_incoterm}`);
    if (constraints.shipment_mode) terms.push(`Shipment: ${constraints.shipment_mode}`);
    if (constraints.compliances?.length) terms.push(`Compliances: ${constraints.compliances.join(', ')}`);
    terms.push('Prices subject to component availability at time of order');

    y = addSectionTitle(doc, 'Terms & Conditions', y);
    y = addText(doc, terms.join('\n'), y, { fontSize: 8 });

    downloadPDF(doc, `${sim.project_name || 'Quote'}_${company}.pdf`);
    toast.success('Quote generated');
  };

  if (loading) return <Layout title="Quote Builder"><div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div></Layout>;
  if (!sim) return <Layout title="Quote Builder"><div className="text-center py-16 text-gray-500">Simulation not found. <Button variant="link" onClick={() => navigate('/project-simulator')}>Go back</Button></div></Layout>;
  if (sim.status === 'processing') return (
    <Layout title="Quote Builder">
      <div className="max-w-3xl mx-auto text-center py-20 space-y-4">
        <Loader2 className="h-12 w-12 text-emerald-500 animate-spin mx-auto" />
        <h3 className="text-xl font-bold text-gray-900">Generating Quote...</h3>
        <p className="text-sm text-gray-500">Pricing components from 1Data market data and applying margin.</p>
      </div>
    </Layout>
  );

  const statusCfg = QUOTE_STATUS_CONFIG[quoteStatus] || QUOTE_STATUS_CONFIG.draft;
  const StatusIcon = statusCfg.icon;

  return (
    <Layout title="Quote Builder" subtitle={sim.project_name || ''} breadcrumbs={[
      { label: 'Quote Builder', path: '/project-simulator' },
      { label: sim.project_name || 'Quote Detail' },
    ]}>
      <div className="space-y-5" data-testid="simulator-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/project-simulator')} className="gap-1 text-gray-600" data-testid="btn-back">
              <ArrowLeft className="h-4 w-4" /> Back to Quotes
            </Button>
            {companyName && <Badge variant="outline" className="text-xs gap-1"><Building2 className="h-3 w-3" /> {companyName}</Badge>}
            <Badge variant="outline" className="text-xs gap-1"><Package className="h-3 w-3" /> {productionQty.toLocaleString()} boards</Badge>
            <Badge className={`text-xs gap-1 border ${statusCfg.color}`} data-testid="quote-status-badge">
              <StatusIcon className="h-3 w-3" /> {statusCfg.label}
            </Badge>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Status Actions */}
            {quoteStatus === 'draft' && (
              <Button variant="outline" size="sm" className="gap-1 text-xs text-blue-700 border-blue-300" onClick={() => openStatusDialog('sent')} data-testid="btn-mark-sent">
                <Send className="h-3.5 w-3.5" /> Mark as Sent
              </Button>
            )}
            {quoteStatus === 'sent' && (
              <>
                <Button variant="outline" size="sm" className="gap-1 text-xs text-green-700 border-green-300" onClick={() => openStatusDialog('accepted')} data-testid="btn-mark-accepted">
                  <Check className="h-3.5 w-3.5" /> Accepted
                </Button>
                <Button variant="outline" size="sm" className="gap-1 text-xs text-red-700 border-red-300" onClick={() => openStatusDialog('rejected')} data-testid="btn-mark-rejected">
                  <XCircle className="h-3.5 w-3.5" /> Rejected
                </Button>
                <Button variant="outline" size="sm" className="gap-1 text-xs text-amber-700 border-amber-300" onClick={() => openStatusDialog('revised')} data-testid="btn-mark-revised">
                  <RotateCcw className="h-3.5 w-3.5" /> Revise
                </Button>
              </>
            )}
            {(quoteStatus === 'rejected' || quoteStatus === 'revised') && (
              <Button variant="outline" size="sm" className="gap-1 text-xs text-gray-600" onClick={() => openStatusDialog('draft')} data-testid="btn-back-to-draft">
                <Edit3 className="h-3.5 w-3.5" /> Back to Draft
              </Button>
            )}
            <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={handleCSVExport} data-testid="btn-export-csv">
              <FileText className="h-3.5 w-3.5" /> Export CSV
            </Button>
            <Button size="sm" className="gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handlePDFExport} data-testid="btn-export-pdf">
              <Download className="h-3.5 w-3.5" /> Generate Quote
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="summary-cards">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-3 pb-2.5">
              <p className="text-[10px] text-gray-500 uppercase">Components</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5">{summary.total_components || 0}</p>
              <p className="text-[10px] text-gray-400">{summary.matched_count || 0} matched</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-3 pb-2.5">
              <p className="text-[10px] text-gray-500 uppercase">Production</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5">{productionQty.toLocaleString()}</p>
              <p className="text-[10px] text-gray-400">boards</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-3 pb-2.5">
              <p className="text-[10px] text-gray-500 uppercase">Material Cost</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5">{format(summary.total_material_cost || 0)}</p>
              <p className="text-[10px] text-gray-400">with price-breaks</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200 shadow-sm">
            <CardContent className="pt-3 pb-2.5">
              <p className="text-[10px] text-emerald-600 uppercase">BOM Quoted</p>
              <p className="text-lg font-bold text-emerald-700 mt-0.5">{format(summary.total_quoted_cost || 0)}</p>
              <p className="text-[10px] text-emerald-400">with {summary.margin_percent || 0}% margin</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200 shadow-sm">
            <CardContent className="pt-3 pb-2.5">
              <p className="text-[10px] text-blue-600 uppercase flex items-center gap-1">Per Board <InfoTip text="Quoted price per board = total quoted / production quantity" /></p>
              <p className="text-lg font-bold text-blue-700 mt-0.5">{format(summary.quoted_per_board || 0)}</p>
              <p className="text-[10px] text-blue-400">cost: {format(summary.cost_per_board || 0)}</p>
            </CardContent>
          </Card>
          {(summary.additional_costs_total || 0) > 0 && (
            <Card className="bg-purple-50 border-purple-200 shadow-sm">
              <CardContent className="pt-3 pb-2.5">
                <p className="text-[10px] text-purple-600 uppercase">Additional Costs</p>
                <p className="text-lg font-bold text-purple-700 mt-0.5">{format(summary.additional_costs_total || 0)}</p>
                <p className="text-[10px] text-purple-400">{additionalCosts.length} item{additionalCosts.length !== 1 ? 's' : ''}</p>
              </CardContent>
            </Card>
          )}
          <Card className={`shadow-sm ${(summary.grand_total || 0) > 0 ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-300' : 'bg-white border-gray-200'}`}>
            <CardContent className="pt-3 pb-2.5">
              <p className="text-[10px] text-emerald-600 uppercase font-semibold">Grand Total</p>
              <p className="text-lg font-bold text-emerald-800 mt-0.5">{format(summary.grand_total || summary.total_quoted_cost || 0)}</p>
              <p className="text-[10px] text-emerald-400">BOM + additional</p>
            </CardContent>
          </Card>
        </div>

        {/* Warnings */}
        {tbdCount > 0 && (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid="tbd-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span><strong>{tbdCount} component{tbdCount > 1 ? 's' : ''}</strong> {unmatchedCount > 0 ? 'unmatched — ' : ''}need manual pricing before this quote is complete. Enter prices below or mark as Excluded.</span>
          </div>
        )}
        {(riskSummary.high || 0) > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700" data-testid="risk-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span><strong>{riskSummary.high} component{riskSummary.high > 1 ? 's' : ''}</strong> flagged as high risk (EOL/NRND, long lead, or single source).</span>
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-xs">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search MPN, CPN, Make..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-input" />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-100 h-9">
            <TabsTrigger value="quote" className="text-xs gap-1" data-testid="tab-quote"><DollarSign className="h-3.5 w-3.5" /> Quote</TabsTrigger>
            <TabsTrigger value="costs" className="text-xs gap-1" data-testid="tab-costs"><Plus className="h-3.5 w-3.5" /> NRE & Costs</TabsTrigger>
            <TabsTrigger value="risk" className="text-xs gap-1" data-testid="tab-risk"><Shield className="h-3.5 w-3.5" /> Risk & Lead</TabsTrigger>
            <TabsTrigger value="alternates" className="text-xs gap-1" data-testid="tab-alternates"><ArrowUpDown className="h-3.5 w-3.5" /> Alternates</TabsTrigger>
            <TabsTrigger value="distributors" className="text-xs gap-1" data-testid="tab-distributors"><Globe className="h-3.5 w-3.5" /> Sources</TabsTrigger>
            <TabsTrigger value="ai" className="text-xs gap-1" data-testid="tab-ai"><Sparkles className="h-3.5 w-3.5" /> AI Recommendations</TabsTrigger>
            <TabsTrigger value="history" className="text-xs gap-1" data-testid="tab-history"><Clock className="h-3.5 w-3.5" /> History</TabsTrigger>
          </TabsList>

          {/* ── QUOTE TAB ── */}
          <TabsContent value="quote">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/80 text-[10px]">
                        <TableHead className="font-semibold sticky left-0 bg-gray-50 z-10">MPN</TableHead>
                        <TableHead className="font-semibold text-center w-14">Status</TableHead>
                        <TableHead className="font-semibold text-right">Qty/Board</TableHead>
                        <TableHead className="font-semibold text-right">Order Qty</TableHead>
                        <TableHead className="font-semibold text-right text-gray-500">Eff. Prob. Price <InfoTip text="Probabilistic price with price-break discount" /></TableHead>
                        <TableHead className="font-semibold text-center w-20 text-purple-700">Markup % <InfoTip text="Margin applied. Default from upload. Click edit to change per-line." /></TableHead>
                        <TableHead className="font-semibold text-right text-emerald-700">Quoted $ <InfoTip text="Final unit price to customer" /></TableHead>
                        <TableHead className="font-semibold text-right text-emerald-700">Line Total</TableHead>
                        <TableHead className="font-semibold text-center w-16">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((r, idx) => {
                        const isEditing = editingLine === r.mpn;
                        const isTbd = r.line_status === 'tbd';
                        const isExcluded = r.line_status === 'excluded';
                        const isManual = r.line_status === 'manual';
                        const hasOverride = r.override_price != null || r.override_margin != null;
                        const lineStatusCfg = LINE_STATUS_CONFIG[r.line_status] || LINE_STATUS_CONFIG.priced;
                        const effectiveMargin = r.override_margin != null ? r.override_margin : (summary.margin_percent || 15);

                        return (
                          <TableRow key={idx} className={`text-[11px] hover:bg-gray-50 ${isExcluded ? 'opacity-40' : ''} ${isTbd ? 'bg-amber-50/40' : ''} ${isManual ? 'bg-blue-50/30' : ''}`} data-testid={`quote-row-${idx}`}>
                            <TableCell className="font-mono font-medium text-gray-900 sticky left-0 bg-white z-10">
                              <div className="flex items-center gap-1.5">
                                {r.locked && <Lock className="h-3 w-3 text-gray-400 shrink-0" />}
                                {!r.matched && <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />}
                                {r.mpn}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={`text-[8px] ${lineStatusCfg.color}`}>{lineStatusCfg.label}</Badge>
                            </TableCell>
                            <TableCell className="text-right">{r.qty_per_board?.toLocaleString() || '-'}</TableCell>
                            <TableCell className="text-right font-medium">{r.order_qty?.toLocaleString() || '-'}</TableCell>
                            <TableCell className="text-right">
                              {r.price_break_discount < 1 ? (
                                <span className="text-blue-700 font-medium">{fmt(r.effective_anchor)} <span className="text-[9px] text-blue-500">-{Math.round((1 - r.price_break_discount) * 100)}%</span></span>
                              ) : <span className="text-gray-500">{fmt(r.effective_anchor)}</span>}
                            </TableCell>
                            <TableCell className="text-center">
                              {isEditing ? (
                                <Input type="number" value={editMargin} onChange={e => setEditMargin(e.target.value)}
                                  placeholder={String(effectiveMargin)} className="h-6 w-14 text-[10px] text-center mx-auto" data-testid={`edit-margin-${idx}`} />
                              ) : (
                                <span className={`font-medium ${r.override_margin != null ? 'text-purple-700' : 'text-gray-500'}`}>
                                  {effectiveMargin}%{r.override_margin != null && <PenLine className="h-2 w-2 inline ml-0.5 text-purple-400" />}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {isEditing ? (
                                <Input type="number" value={editPrice} onChange={e => setEditPrice(e.target.value)}
                                  placeholder={r.quoted_price?.toFixed(4)} className="h-6 w-20 text-[11px] text-right" data-testid={`edit-price-${idx}`} />
                              ) : isTbd ? (
                                <ManualPriceInput mpn={r.mpn} onSave={saveManualPrice} saving={saving} idx={idx} />
                              ) : (
                                <span className={`font-semibold ${hasOverride ? 'text-blue-700' : 'text-emerald-700'}`}>
                                  {fmt(r.quoted_price)} {r.override_price != null && <PenLine className="h-2.5 w-2.5 inline text-blue-400" />}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-emerald-700">{fmtTotal(r.line_total)}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center gap-0.5 justify-center">
                                {isEditing ? (
                                  <>
                                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-green-600" onClick={() => saveLineOverride(r.mpn)} disabled={saving} data-testid={`save-line-${idx}`}>
                                      <Check className="h-3 w-3" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400" onClick={() => setEditingLine(null)} data-testid={`cancel-edit-${idx}`}>
                                      <XIcon className="h-3 w-3" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    {!isExcluded && !isTbd && (
                                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600" onClick={() => startEditLine(r)} title="Edit price/margin" data-testid={`btn-edit-line-${idx}`}>
                                        <Edit3 className="h-3 w-3" />
                                      </Button>
                                    )}
                                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600" onClick={() => toggleLock(r)} title={r.locked ? 'Unlock' : 'Lock'} data-testid={`btn-lock-${idx}`}>
                                      {r.locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                                    </Button>
                                    {!isExcluded ? (
                                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-red-500" onClick={() => setLineStatus(r.mpn, 'excluded')} title="Exclude" data-testid={`btn-exclude-${idx}`}>
                                        <XCircle className="h-3 w-3" />
                                      </Button>
                                    ) : (
                                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-green-600" onClick={() => setLineStatus(r.mpn, r.matched ? 'priced' : 'tbd')} title="Restore" data-testid={`btn-restore-${idx}`}>
                                        <RotateCcw className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-400 text-sm">No components found</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── NRE & ADDITIONAL COSTS TAB ── */}
          <TabsContent value="costs">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">NRE & Additional Costs</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Non-BOM costs: engineering, tooling, assembly labor, logistics, etc.</p>
                  </div>
                  <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setShowAddCost(true)} data-testid="btn-add-cost">
                    <Plus className="h-3.5 w-3.5" /> Add Cost
                  </Button>
                </div>

                {additionalCosts.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/80 text-[10px]">
                        <TableHead className="font-semibold">Item</TableHead>
                        <TableHead className="font-semibold">Type</TableHead>
                        <TableHead className="font-semibold text-right">Amount</TableHead>
                        <TableHead className="font-semibold text-right">Total for Order</TableHead>
                        <TableHead className="font-semibold">Note</TableHead>
                        <TableHead className="font-semibold w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {additionalCosts.map((c, i) => {
                        const orderTotal = c.cost_type === 'per_board' ? c.amount * productionQty : c.amount;
                        return (
                          <TableRow key={c.cost_id || i} className="text-xs" data-testid={`cost-row-${i}`}>
                            <TableCell className="font-medium text-gray-800">{c.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[9px]">{c.cost_type === 'per_board' ? `Per Board ($${c.amount}/board)` : 'One-Time'}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-semibold">${c.amount.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-semibold text-purple-700">${orderTotal.toLocaleString()}</TableCell>
                            <TableCell className="text-gray-500 max-w-[200px] truncate">{c.note || '-'}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-gray-400 hover:text-red-500" onClick={() => removeCost(c.cost_id)} data-testid={`btn-remove-cost-${i}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow className="bg-gray-50 font-semibold text-xs">
                        <TableCell colSpan={3} className="text-right text-gray-600">Additional Costs Total:</TableCell>
                        <TableCell className="text-right text-purple-700">${(summary.additional_costs_total || 0).toLocaleString()}</TableCell>
                        <TableCell colSpan={2}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-10 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
                    <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    No additional costs added yet.
                  </div>
                )}

                {/* Summary */}
                <div className="grid grid-cols-3 gap-3 pt-2 border-t border-gray-200">
                  <div className="text-center">
                    <p className="text-[10px] text-gray-500 uppercase">BOM Quoted</p>
                    <p className="text-lg font-bold text-emerald-700">{format(summary.total_quoted_cost || 0)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-gray-500 uppercase">Additional Costs</p>
                    <p className="text-lg font-bold text-purple-700">{format(summary.additional_costs_total || 0)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-emerald-600 uppercase font-semibold">Grand Total</p>
                    <p className="text-lg font-bold text-emerald-800">{format(summary.grand_total || summary.total_quoted_cost || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── RISK TAB ── */}
          <TabsContent value="risk">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/80 text-[10px]">
                        <TableHead className="font-semibold">MPN</TableHead>
                        <TableHead className="font-semibold text-center">Matched</TableHead>
                        <TableHead className="font-semibold">Lifecycle</TableHead>
                        <TableHead className="font-semibold text-right">Lead Time</TableHead>
                        <TableHead className="font-semibold text-center">Availability</TableHead>
                        <TableHead className="font-semibold text-center">Single Source</TableHead>
                        <TableHead className="font-semibold text-center">Risk</TableHead>
                        <TableHead className="font-semibold text-center">Datasheet</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((r, idx) => (
                        <TableRow key={idx} className={`text-xs hover:bg-gray-50 ${r.risk_level === 'high' ? 'bg-red-50/30' : ''}`} data-testid={`risk-row-${idx}`}>
                          <TableCell className="font-mono font-medium text-gray-900">{r.mpn}</TableCell>
                          <TableCell className="text-center">
                            {r.matched ? <CheckCircle className="h-4 w-4 text-green-600 mx-auto" /> : <XIcon className="h-4 w-4 text-red-400 mx-auto" />}
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-[9px] ${r.lifecycle_status === 'Active' ? 'bg-green-100 text-green-800' : r.lifecycle_status === 'NRND' ? 'bg-amber-100 text-amber-800' : r.lifecycle_status === 'EOL' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}`}>
                              {r.lifecycle_status || 'Unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{r.lead_time_weeks ? `${r.lead_time_weeks} weeks` : '-'}</TableCell>
                          <TableCell className="text-center">
                            <Badge className={`text-[9px] ${r.availability === 'In Stock' ? 'bg-green-100 text-green-800' : r.availability === 'Limited' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                              {r.availability || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {r.single_source ? (
                              <span className="group relative inline-flex items-center gap-1 text-amber-600 font-medium">
                                <AlertTriangle className="h-3.5 w-3.5" /> Yes
                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-gray-900 text-white text-[10px] rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                                  Single source — only 1 manufacturer/distributor. Supply risk is higher.
                                </span>
                              </span>
                            ) : (
                              <span className="text-green-600 font-medium">No</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={`text-[9px] ${riskColors[r.risk_level] || 'bg-gray-100 text-gray-700'}`}>{r.risk_level || '-'}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {r.datasheet_url ? <a href={r.datasheet_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5 text-[10px]"><ExternalLink className="h-3 w-3" /> View</a> : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── ALTERNATES TAB ── */}
          <TabsContent value="alternates">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/80 text-[10px]">
                        <TableHead className="font-semibold">Original MPN</TableHead>
                        <TableHead className="font-semibold">Alternate MPN</TableHead>
                        <TableHead className="font-semibold">Make</TableHead>
                        <TableHead className="font-semibold">Type</TableHead>
                        <TableHead className="font-semibold text-right">Alt Prob. Price $</TableHead>
                        <TableHead className="font-semibold text-right">Alt Quoted $</TableHead>
                        <TableHead className="font-semibold text-right">Savings %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.flatMap((r, ridx) =>
                        (r.alternates || []).map((alt, aidx) => (
                          <TableRow key={`${ridx}-${aidx}`} className="text-xs hover:bg-gray-50" data-testid={`alt-row-${ridx}-${aidx}`}>
                            <TableCell className="font-mono font-medium text-gray-900">{r.mpn}</TableCell>
                            <TableCell className="font-mono text-blue-700">{alt.mpn}</TableCell>
                            <TableCell className="text-gray-500">{alt.make || '-'}</TableCell>
                            <TableCell><Badge variant="outline" className="text-[9px]">{alt.type}</Badge></TableCell>
                            <TableCell className="text-right">{fmt(alt.anchor_price)}</TableCell>
                            <TableCell className="text-right font-semibold text-emerald-700">{fmt(alt.quoted_price)}</TableCell>
                            <TableCell className="text-right">
                              {alt.savings_pct > 0 ? <span className="text-green-700 font-medium">{alt.savings_pct}%</span> : <span className="text-gray-400">0%</span>}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                      {filtered.every(r => !r.alternates?.length) && (
                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400 text-sm">No alternates available</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── DISTRIBUTOR SOURCES TAB ── */}
          <TabsContent value="distributors">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-4 space-y-3">
                {filtered.map((r, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`dist-section-${idx}`}>
                    <button className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                      onClick={() => setExpandedMpn(expandedMpn === r.mpn ? null : r.mpn)}>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-medium text-sm text-gray-900">{r.mpn}</span>
                        <Badge variant="outline" className="text-[10px]">{(r.distributor_pricing || []).length} sources</Badge>
                        <Badge className={`text-[9px] ${riskColors[r.risk_level] || ''}`}>{r.risk_level}</Badge>
                      </div>
                      {expandedMpn === r.mpn ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                    </button>
                    {expandedMpn === r.mpn && (
                      <div className="border-t border-gray-200">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50/60 text-[10px]">
                              <TableHead className="font-semibold">Distributor</TableHead>
                              <TableHead className="font-semibold">Country</TableHead>
                              <TableHead className="font-semibold">Price</TableHead>
                              <TableHead className="font-semibold">Type</TableHead>
                              <TableHead className="font-semibold">Rate</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(r.distributor_pricing || []).map((dp, di) => (
                              <TableRow key={di} className="text-xs">
                                <TableCell className="font-medium">{dp.distributor_name}</TableCell>
                                <TableCell>{dp.country_of_supply}</TableCell>
                                <TableCell className="font-semibold text-green-700">{fmt(dp.price)}</TableCell>
                                <TableCell><Badge className={`text-[9px] ${dp.distributor_type === 'authorized' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>{dp.distributor_type}</Badge></TableCell>
                                <TableCell><Badge className={`text-[9px] ${dp.rate_type === 'Spot' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>{dp.rate_type}</Badge></TableCell>
                              </TableRow>
                            ))}
                            {(r.distributor_pricing || []).length === 0 && (
                              <TableRow><TableCell colSpan={5} className="text-center py-4 text-gray-400 text-xs">No pricing data</TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── AI RECOMMENDATIONS TAB ── */}
          <TabsContent value="ai">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-5 space-y-4">
                {!recommendations && !recoLoading ? (
                  <div className="text-center py-10 space-y-3" data-testid="ai-reco-empty">
                    <Sparkles className="h-10 w-10 text-blue-400 mx-auto" />
                    <h4 className="font-semibold text-gray-800">AI Quoting Recommendations</h4>
                    <p className="text-sm text-gray-500 max-w-md mx-auto">Get AI-powered quoting strategy: margin optimization, risk mitigation, and alternate opportunities.</p>
                    <Button onClick={() => fetchRecommendations()} className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5" data-testid="btn-generate-reco">
                      <Sparkles className="h-4 w-4" /> Generate Recommendations
                    </Button>
                  </div>
                ) : recoLoading ? (
                  <div className="text-center py-10">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
                    <p className="text-sm text-gray-500 mt-3">Generating AI recommendations...</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-800">AI Quoting Strategy</h4>
                        <p className="text-sm text-gray-500 mt-0.5">{recommendations.summary}</p>
                      </div>
                      <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => fetchRecommendations(true)} data-testid="btn-regenerate-reco">
                        <Sparkles className="h-3.5 w-3.5" /> Regenerate
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {(recommendations.recommendations || []).map((rec, i) => {
                        const catColors = { margin_optimization: 'bg-emerald-100 text-emerald-800', risk_mitigation: 'bg-red-100 text-red-800', unmatched_resolution: 'bg-amber-100 text-amber-800', alternate_opportunity: 'bg-blue-100 text-blue-800', volume_leverage: 'bg-purple-100 text-purple-800', cost_review: 'bg-gray-100 text-gray-700' };
                        const prioColors = { critical: 'bg-red-600 text-white', high: 'bg-orange-100 text-orange-800', medium: 'bg-yellow-100 text-yellow-800' };
                        return (
                          <div key={i} className="border border-gray-200 rounded-lg p-3.5" data-testid={`reco-card-${i}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge className={`text-[9px] ${catColors[rec.category] || catColors.cost_review}`}>{(rec.category || '').replace(/_/g, ' ')}</Badge>
                                  <Badge className={`text-[9px] ${prioColors[rec.priority] || prioColors.medium}`}>{rec.priority}</Badge>
                                  {rec.estimated_savings && <Badge variant="outline" className="text-[9px] text-green-700 border-green-300">{rec.estimated_savings}</Badge>}
                                </div>
                                <p className="text-sm font-medium text-gray-800 mt-1.5">{rec.title}</p>
                                <p className="text-xs text-gray-500 mt-1">{rec.description}</p>
                                {rec.affected_components?.length > 0 && (
                                  <div className="flex gap-1 flex-wrap mt-1.5">
                                    {rec.affected_components.slice(0, 4).map((mpn, j) => (
                                      <Badge key={j} variant="outline" className="text-[9px] font-mono">{mpn}</Badge>
                                    ))}
                                    {rec.affected_components.length > 4 && <Badge variant="outline" className="text-[9px]">+{rec.affected_components.length - 4}</Badge>}
                                  </div>
                                )}
                              </div>
                              <Button variant="outline" size="sm" className="gap-1 text-[10px] shrink-0" onClick={() => {
                                setAssignContext({ sourceModule: 'project_simulator', bomName: sim.project_name, recommendation: `${rec.title}: ${rec.description}`, mpn: rec.affected_components?.[0] || '' });
                                setAssignOpen(true);
                              }} data-testid={`btn-assign-reco-${i}`}>
                                <UserPlus className="h-3 w-3" /> Assign
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {(recommendations.recommendations || []).length === 0 && <p className="text-center text-gray-400 text-sm py-6">No recommendations generated.</p>}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── STATUS HISTORY TAB ── */}
          <TabsContent value="history">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-5 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800">Quote Status History</h3>
                {statusHistory.length > 0 ? (
                  <div className="space-y-3">
                    {[...statusHistory].reverse().map((h, i) => {
                      const cfg = QUOTE_STATUS_CONFIG[h.status] || QUOTE_STATUS_CONFIG.draft;
                      const Icon = cfg.icon;
                      return (
                        <div key={i} className="flex items-start gap-3 text-sm" data-testid={`history-entry-${i}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${cfg.color}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{cfg.label}</p>
                            {h.note && <p className="text-xs text-gray-500 mt-0.5">{h.note}</p>}
                            <p className="text-[10px] text-gray-400 mt-0.5">{new Date(h.timestamp).toLocaleString()}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No status changes recorded.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ── ADD COST DIALOG ── */}
        <Dialog open={showAddCost} onOpenChange={setShowAddCost}>
          <DialogContent className="max-w-md" data-testid="add-cost-dialog">
            <DialogHeader>
              <DialogTitle className="text-base">Add Additional Cost</DialogTitle>
              <DialogDescription className="text-xs">NRE, tooling, assembly, logistics, or any non-BOM cost.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs font-medium">Cost Name <span className="text-red-500">*</span></Label>
                <Input value={newCostName} onChange={e => setNewCostName(e.target.value)} placeholder="e.g. NRE, Tooling, Assembly Labor" className="mt-1 h-9 text-sm" data-testid="cost-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium">Type</Label>
                  <Select value={newCostType} onValueChange={setNewCostType}>
                    <SelectTrigger className="mt-1 h-9 text-sm" data-testid="cost-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_time">One-Time</SelectItem>
                      <SelectItem value="per_board">Per Board</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium">Amount ($) <span className="text-red-500">*</span></Label>
                  <Input type="number" value={newCostAmount} onChange={e => setNewCostAmount(e.target.value)} placeholder="0.00" min="0" step="0.01" className="mt-1 h-9 text-sm" data-testid="cost-amount-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium">Note</Label>
                <Input value={newCostNote} onChange={e => setNewCostNote(e.target.value)} placeholder="Optional description" className="mt-1 h-9 text-sm" data-testid="cost-note-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddCost(false)}>Cancel</Button>
              <Button onClick={addCost} disabled={!newCostName.trim() || !newCostAmount} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-save-cost">
                <Plus className="h-4 w-4 mr-1" /> Add Cost
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── STATUS CHANGE DIALOG ── */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent className="max-w-sm" data-testid="status-dialog">
            <DialogHeader>
              <DialogTitle className="text-base">Update Quote Status</DialogTitle>
              <DialogDescription className="text-xs">
                Mark this quote as <strong>{QUOTE_STATUS_CONFIG[pendingStatus]?.label || pendingStatus}</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs font-medium">Note (optional)</Label>
                <Input value={statusNote} onChange={e => setStatusNote(e.target.value)} placeholder="e.g. Sent to OEM via email" className="mt-1 h-9 text-sm" data-testid="status-note-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
              <Button onClick={updateQuoteStatus} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-confirm-status">
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {assignOpen && <QuickAssignModal open={assignOpen} onClose={() => setAssignOpen(false)} context={assignContext} />}
      </div>
    </Layout>
  );
};

// ─── Manual Price Input for TBD/Unmatched Lines ───
const ManualPriceInput = ({ mpn, onSave, saving, idx }) => {
  const [price, setPrice] = useState('');
  return (
    <div className="flex items-center gap-1">
      <Input type="number" value={price} onChange={e => setPrice(e.target.value)}
        placeholder="Enter $" className="h-6 w-20 text-[11px] text-right border-amber-300" data-testid={`manual-price-${idx}`} />
      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-amber-600 hover:text-green-600" disabled={!price || saving}
        onClick={() => onSave(mpn, price)} data-testid={`btn-save-manual-${idx}`}>
        <Check className="h-3 w-3" />
      </Button>
    </div>
  );
};

export default SimulatorDetail;
